import {createSelector} from "reselect"
import {pathEq, contains, omit, prop, path, pipe} from "ramda"
import {first, updatePath} from "hurdak/src/core"
import storage from "hurdak/src/storage"
import {fuzzy} from "utils/misc"
import {Dt, guessTimezone, guessLocale} from "utils/dt"
import Api from "modules/domain/api"
import {locationMatches} from "modules/domain"
import {compileBindle} from "modules/bindles"
import Db from "modules/db/state"
import history from "utils/history"
import {CLEAR_ROOT_STATE} from "app/core/constants"
import Toast from "app/toast/state"

let maintenanceModeTimeout = null

const authStorage = storage.cursor("core/auth", {
  sessionId: null,
  impersonation: null,
})

const deviceStorage = storage.cursor("core/device", {
  tillId: null,
})

const getInitialState = () => ({
  online: true,
  whoami: null,
  location: history.location,
  maintenanceMode: false,
  forceRefresh: false,
  lastStoreId: null,
  auth: authStorage.get(),
  device: deviceStorage.get(),
})

export default compileBindle("core", {
  initialState: getInitialState(),
  reducers: {
    // Same as setState, but used in middleware
    setSynced: (state, payload) => ({...state, ...payload}),
  },
  createSelectors: ({select}) => {
    const selectStore = pipe(select.whoami, path(["store"]))
    const selectModel = pipe(select.whoami, path(["model"]))
    const selectProPay = pipe(select.whoami, path(["proPay"]))
    const selectShopify = pipe(select.whoami, path(["shopify"]))
    const selectBilling = pipe(select.whoami, path(["billing"]))
    const selectUserId = pipe(select.whoami, path(["session", "user"]))

    const selectSessionId = pipe(select.auth, path(["sessionId"]))
    const selectImpersonating = pipe(select.auth, path(["impersonating"]))

    const selectUser = createSelector(selectUserId, Db.select.interface, (userId, db) =>
      db.oneActive(userId),
    )

    const selectDefaultLabel = createSelector(
      selectStore,
      Db.select.interface,
      ({defaultLabel}, db) => defaultLabel || prop("id", first(db.all("label"))),
    )

    const selectLocationTills = createSelector(Db.select.interface, selectUser, (db, user) =>
      db.active("till").filter(locationMatches(user)),
    )

    const selectSearchLocationTills = createSelector(selectLocationTills, tills =>
      fuzzy(tills, {keys: ["name"], threshold: 0.3}),
    )

    const selectLocationShelves = createSelector(Db.select.interface, selectUser, (db, user) =>
      db.active("shelf").filter(locationMatches(user)),
    )

    const selectSearchLocationShelves = createSelector(selectLocationShelves, shelves =>
      fuzzy(shelves, {keys: ["name"], threshold: 0.3}),
    )

    const selectLocale = state => prop("locale", selectStore(state)) || guessLocale()
    const selectTimezone = state => prop("timezone", selectStore(state)) || guessTimezone()
    const selectCountry = state => prop("country", selectStore(state)) || "USA"
    const selectCurrencySymbol = state => prop("currencySymbol", selectStore(state)) || "$"

    const selectIsUS = createSelector(
      selectCountry,
      selectCurrencySymbol,
      (country, currencySymbol) =>
        currencySymbol === "$" &&
        contains(country.toLowerCase(), ["us", "usa", "united states", "united states of america"]),
    )

    const selectIsDemoStore = createSelector(
      selectStore,
      ({email}) => email === "demo@consigncloud.com",
    )

    const selectIsRecentSignup = createSelector(selectStore, ({created}) =>
      Dt.parse(created).isAfter(Dt.now().subtract(30, "days")),
    )

    return {
      store: selectStore,
      model: selectModel,
      proPay: selectProPay,
      shopify: selectShopify,
      billing: selectBilling,
      sessionId: selectSessionId,
      impersonating: selectImpersonating,
      user: selectUser,
      defaultLabel: selectDefaultLabel,
      locationTills: selectLocationTills,
      searchLocationTills: selectSearchLocationTills,
      locationShelves: selectLocationShelves,
      searchLocationShelves: selectSearchLocationShelves,
      locale: selectLocale,
      country: selectCountry,
      timezone: selectTimezone,
      currencySymbol: selectCurrencySymbol,
      till: createSelector(select.device, Db.select.interface, ({tillId}, db) =>
        db.oneActive(tillId),
      ),
      isUS: selectIsUS,
      isDemoStore: selectIsDemoStore,
      isRecentSignup: selectIsRecentSignup,
      printnodeKey: pipe(selectStore, prop("printnodeKey")),
      useEmergepay: createSelector(selectStore, ({creditCardImplementation, emergepayConfig}) =>
        Boolean(creditCardImplementation === "emergepay" && emergepayConfig.length),
      ),
      useProPay: createSelector(selectStore, ({creditCardImplementation, proPayStatus}) =>
        Boolean(creditCardImplementation === "pro_pay" && proPayStatus === "active"),
      ),
      anywhereCommerceTerminals: pipe(selectStore, prop("anywhereCommerceTerminals")),
      getUserSetting: createSelector(
        Db.select.interface,
        selectUser,
        (db, user) =>
          ({ns, ...info}, defaultValue) =>
            db.setting({ns: `${ns}/${user.id}`, ...info}, defaultValue),
      ),
    }
  },
  createActions: ({select, actions: {setSynced, setState}}) => {
    const loadWhoami = () => async (dispatch, getState) => {
      const {lastStoreId, device} = select.root(getState())
      const {db, ...whoami} = await dispatch(Api.store.get("whoami"))

      // Duplicate our db in a separate, more useful store
      dispatch(Db.setState(db))

      // If they're logging in with a different store, they lose all their saved
      // data from last session
      const resetDevice = lastStoreId && whoami.store.id !== lastStoreId
      const newDevice = resetDevice ? {} : device

      // Set whoami last, since App.js transitions to another screen that needs the
      // above info once userId is set.
      dispatch(setSynced({whoami, device: newDevice, lastStoreId: whoami.store.id}))
    }

    const setAuth = auth => async (dispatch, getState) => {
      const newAuth = {...select.auth(getState()), ...auth}

      authStorage.set(newAuth)

      dispatch(setSynced({auth: newAuth}))

      await dispatch(loadWhoami())
    }

    const saveToDevice = device => (dispatch, getState) => {
      const newDevice = {...select.device(getState()), ...device}

      deviceStorage.set(newDevice)

      dispatch(setSynced({device: newDevice}))
    }

    const loginUser = params => async dispatch => {
      await dispatch(Api.session.post("user", params))
      await dispatch(loadWhoami())
    }

    const logoutUser = () => async (dispatch, getState) => {
      const state = omit(["user"], select.root(getState()))

      // Hacky, but we want the logout to happen immediately
      if (state.whoami) {
        dispatch(setSynced(updatePath(["whoami", "session"], state)))
      }

      await dispatch(Api.session.delete("user"))
      await dispatch(loadWhoami())
    }

    const putSetting =
      ({ns, key, value}) =>
      async (dispatch, getState) => {
        const setting = Db.select.interface(getState()).setting({ns, key})

        const {id} = setting.id
          ? await dispatch(Api.setting.update(setting.id, {value}))
          : await dispatch(Api.setting.create({...setting, value}))

        // Patch it into our settings rather than reload whoami
        dispatch(Db.put("setting", {...setting, id, value}))
      }

    const putUserSetting =
      ({ns, key, value}) =>
      (dispatch, getState) => {
        const user = select.user(getState())

        // We delay this by 3 seconds, so avoid race conditions on user logout
        if (user) {
          return dispatch(putSetting({ns: `${ns}/${user.id}`, key, value}))
        }
      }

    return {
      loadWhoami,
      setAuth,
      saveToDevice,
      logoutUser,
      loginUser,
      putSetting,
      putUserSetting,
      resetAppState: () => (dispatch, getState) => {
        const device = select.device(getState())
        const store = select.store(getState())
        const lastStoreId = prop("id", store)

        // Delete the session on the server
        dispatch(Api.session.delete())

        // Clear out everything, not just the core ns
        dispatch({type: CLEAR_ROOT_STATE})

        // Clear localstorage; there are user settings we want to survive
        // across sessions however.
        storage.clear({
          keep: storage
            .keys()
            .filter(
              key =>
                key === "core/device" ||
                key === "emergepay-device" ||
                key.includes("visibleColumns") ||
                key.includes("modules/printing") ||
                key.includes("anywhere-commerce") ||
                key.includes("browserUpgradeModalOpen"),
            ),
        })

        // Keep device settings so we remember their printer etc but clear out auth information
        dispatch(setSynced({...getInitialState(), device, lastStoreId}))
      },
      onboard: (ns, name, state) => async (dispatch, getState) => {
        const {onboarding} = select.store(getState())

        // If nothing is changing, don't send off unnecessary requests
        if (pathEq([ns, name], state, onboarding)) {
          return
        }

        await dispatch(Api.store.post("onboard", {ns, name, state}))
        await dispatch(loadWhoami())
      },
      showMaintenanceMode: () => (dispatch, getState) => {
        dispatch(setState({maintenanceMode: true}))

        async function check() {
          maintenanceModeTimeout = null

          await dispatch(loadWhoami())

          dispatch(setState({maintenanceMode: false}))

          // don't set another timeout; that'll be handled by the loadWhoami request failing
        }

        // If we already have a timeout, no need to set another
        if (!maintenanceModeTimeout) {
          maintenanceModeTimeout = setTimeout(check, 30 * 1000)
        }
      },
      showFeatureUnavailable: () =>
        Toast.push({
          type: "failure",
          message:
            "The system is currently under load, some features will be temporarily unavailable.",
        }),
      reloadingAppState: thunk => async dispatch => {
        const result = await dispatch(thunk)

        await dispatch(loadWhoami())

        return result
      },
    }
  },
})
